import { api } from '../../../api/client';
import type { DemoSchool, IntegrityChecklist, SimulationActor } from '../types';

export const simulationApi = {
  /**
   * Busca todas as escolas de demonstração disponíveis.
   */
  getDemoSchools: async (): Promise<DemoSchool[]> => {
    const { data } = await api.get<DemoSchool[]>('/simulation/schools');
    return data;
  },

  /**
   * Valida a integridade da estrutura escolar da escola selecionada.
   */
  validateSchool: async (escolaId: string): Promise<IntegrityChecklist> => {
    const { data } = await api.get<IntegrityChecklist>(`/simulation/schools/${escolaId}/validate`);
    return data;
  },

  /**
   * Obtém a lista de integrantes e contas de acesso disponíveis para a escola.
   */
  getSchoolActors: async (escolaId: string): Promise<SimulationActor[]> => {
    const { data } = await api.get<SimulationActor[]>(`/simulation/schools/${escolaId}/users`);
    return data;
  },

  /**
   * Responsável autenticável vinculado a aluno da turma do professor (push na chamada).
   */
  getPairedResponsavel: async (
    escolaId: string,
    professorId?: string,
  ): Promise<{
    found: boolean;
    responsavel: (SimulationActor & {
      responsavelId?: string;
      professorId?: string;
      alunoId?: string;
      alunoNome?: string;
      turmaId?: string | null;
    }) | null;
  }> => {
    const { data } = await api.get(`/simulation/schools/${escolaId}/paired-responsavel`, {
      params: professorId ? { professorId } : undefined,
    });
    return data;
  },

  getCockpitMobileOptions: async (
    escolaId: string,
    professorId?: string,
  ): Promise<{
    professors: Array<{ id: string; nome: string; email: string | null; fotoUrl?: string | null }>;
    selectedProfessorId: string | null;
    turmas: Array<{
      id: string;
      nome: string;
      serie: string;
      turno: string;
      responsaveis: Array<{
        id: string;
        responsavelId: string;
        nome: string;
        email: string;
        fotoUrl?: string | null;
        alunoId: string;
        alunoNome: string;
      }>;
    }>;
    scope?: 'professor' | 'escola' | 'none';
  }> => {
    const { data } = await api.get(`/simulation/schools/${escolaId}/cockpit-mobile-options`, {
      params: professorId ? { professorId } : undefined,
    });
    return data;
  },

  /**
   * Prepara tokens de sessão para a escola.
   * Se actorIds for passado (até 40), o backend respeita a seleção (inclui não-fixos).
   */
  prepareSession: async (
    escolaId: string,
    actorIds?: string[],
    simulationSessionId?: string,
  ): Promise<any> => {
    const body: {
      escolaId: string;
      actorIds?: string[];
      simulationSessionId?: string;
    } = { escolaId };
    if (actorIds?.length) body.actorIds = actorIds.slice(0, 40);
    if (simulationSessionId) body.simulationSessionId = simulationSessionId;
    const { data } = await api.post('/simulation/start', body);
    return data;
  },

  getSessionClock: async (sessionId: string) => {
    const { data } = await api.get(`/simulation/sessions/${sessionId}/clock`);
    return data as {
      simDateYmd: string;
      virtualEpochMs: number;
      speed: number;
      playing: boolean;
      updatedAt: string;
    };
  },

  patchSessionClock: async (
    sessionId: string,
    patch: {
      simDateYmd?: string;
      virtualEpochMs?: number;
      speed?: number;
      playing?: boolean;
    },
  ) => {
    const { data } = await api.patch(`/simulation/sessions/${sessionId}/clock`, patch);
    return data as {
      simDateYmd: string;
      virtualEpochMs: number;
      speed: number;
      playing: boolean;
      updatedAt: string;
    };
  },

  /** Relógio da sessão amarrada ao JWT (PWA / iframe). */
  getMyClock: async () => {
    const { data } = await api.get('/simulation/clock');
    return data as {
      simDateYmd: string;
      virtualEpochMs: number;
      speed: number;
      playing: boolean;
      updatedAt: string;
      sessionId: string | null;
      source: 'session' | 'wall';
    };
  },

  claimDevicePair: async (token: string, simulationSessionId: string, escolaId?: string) => {
    const { data } = await api.post('/simulation/device-pair/claim', {
      token,
      simulationSessionId,
      escolaId,
    });
    return data as {
      access_token: string;
      user: Record<string, unknown>;
    };
  },

  buildDevicePairRedirectUrl: (opts: {
    actorId: string;
    sessionId: string;
    origin?: string;
    promptNotification?: boolean;
  }) => {
    const origin = opts.origin || window.location.origin;
    const params = new URLSearchParams({
      actorId: opts.actorId,
      sessionId: opts.sessionId,
      origin,
    });
    if (opts.promptNotification !== false) params.set('promptNotification', '1');
    return `${origin}/api/v1/simulation/magic-login-redirect?${params.toString()}`;
  },


  /**
   * Executa a criação de dados de demonstração (Seed) programaticamente.
   */
  executeDemoSeed: async (cleanManualSchools = false): Promise<any> => {
    const { data } = await api.post('/simulation/seed', { cleanManualSchools });
    return data;
  },

  /**
   * Busca os logs do processo de seed em tempo real.
   */
  getSeedLogs: async (): Promise<{ active: boolean; logs: string[] }> => {
    const { data } = await api.get<{ active: boolean; logs: string[] }>('/simulation/seed/logs');
    return data;
  },

  /**
   * Inicia o cenário automático selecionado.
   */
  startScenario: async (scenarioId: string, escolaId: string): Promise<any> => {
    const { data } = await api.post('/simulation/scenarios/start', { scenarioId, escolaId });
    return data;
  },

  /**
   * Interrompe o cenário automático atual.
   */
  stopScenario: async (): Promise<any> => {
    const { data } = await api.post('/simulation/scenarios/stop');
    return data;
  },

  /**
   * Busca as cidades suportadas para simulação.
   */
  getCities: async (): Promise<any[]> => {
    const { data } = await api.get<any[]>('/simulation/cities');
    return data;
  },

  /**
   * Obtém a configuração de escolas para a cidade.
   */
  getCityConfig: async (cityId: string): Promise<any[]> => {
    const { data } = await api.get<any[]>(`/simulation/cities/${cityId}/config`);
    return data;
  },

  /**
   * Executa o onboarding rápido para a cidade.
   */
  onboardCity: async (cityId: string, cityName: string, uf: string, schools: { id?: string; nome: string }[]): Promise<any> => {
    const { data } = await api.post(`/simulation/cities/${cityId}/onboard`, { cityName, uf, schools });
    return data;
  },

  /**
   * Obtém metadados de simulação de uma escola.
   */
  getSchoolInfo: async (escolaId: string): Promise<any> => {
    const { data } = await api.get<any>(`/simulation/schools/${escolaId}/info`);
    return data;
  },

  /**
   * Cria uma nova escola demo com seed automático.
   * Apenas nome, municipioId e nAlunos são obrigatórios — todo o resto é derivado.
   */
  createDemoSchool: async (nome: string, municipioId: string, nAlunos: number): Promise<DemoSchool> => {
    const { data } = await api.post<DemoSchool>('/simulation/schools/create', { nome, municipioId, nAlunos });
    return data;
  },

  getSchoolPanelDetail: async (escolaId: string): Promise<any> => {
    const { data } = await api.get<any>(`/simulation/schools/${escolaId}/panel-detail`);
    return data;
  },

  getShiftDayPlan: async (
    escolaId: string,
    turnoId: string,
    diaSemana: string,
  ): Promise<any> => {
    const { data } = await api.get(`/simulation/schools/${escolaId}/shift-day-plan`, {
      params: { turnoId, diaSemana },
    });
    return data;
  },

  getAtoresFixos: async (): Promise<any> => {
    const { data } = await api.get<any>('/simulation/atores-fixos');
    return data;
  },

  getOverviewPanel: async (escolaIds: string[]): Promise<any> => {
    const { data } = await api.get<any>(`/simulation/overview?escolaIds=${escolaIds.join(',')}`);
    return data;
  },

  disconnectDevice: async (actorId: string): Promise<any> => {
    const { data } = await api.post('/simulation/disconnect-device', { actorId });
    return data;
  },

  sendTestPush: async (actorId: string): Promise<any> => {
    const { data } = await api.post('/simulation/send-test-push', { actorId });
    return data;
  },

  /** Turnos operacionais da escola (ids reais para Chronos). */
  listSchoolTurnos: async (
    escolaId: string,
  ): Promise<
    Array<{
      id: string;
      nome: string;
      ativo?: boolean;
      horaAberturaPortoes?: string;
      horaEntradaOficial?: string;
      horaSaidaOficial?: string;
      horaFechamentoPortoes?: string;
    }>
  > => {
    const { data } = await api.get(`/escolas/${escolaId}/turnos`);
    return Array.isArray(data) ? data : [];
  },

  chronosClean: async (escolaId: string, dataRef?: string) => {
    const { data } = await api.post('/dev/chronos-engine/clean', {
      escolaId,
      data: dataRef,
    });
    return data;
  },

  chronosStart: async (body: {
    escolaId: string;
    turnoId: string;
    realDurationSec: number;
    pctEntrada?: number;
    pctAusente?: number;
    pctAtrasado?: number;
    skipChamadas?: boolean;
  }) => {
    const { data } = await api.post('/dev/chronos-engine/start', body);
    return data;
  },

  chronosStop: async (escolaId?: string, turnoId?: string) => {
    const { data } = await api.post('/dev/chronos-engine/stop', {
      escolaId,
      turnoId,
    });
    return data;
  },

  chronosPause: async (escolaId: string, turnoId: string) => {
    const { data } = await api.post('/dev/chronos-engine/pause', {
      escolaId,
      turnoId,
    });
    return data;
  },

  chronosResume: async (escolaId: string, turnoId: string) => {
    const { data } = await api.post('/dev/chronos-engine/resume', {
      escolaId,
      turnoId,
    });
    return data;
  },

  chronosState: async (escolaId: string, turnoId: string) => {
    const { data } = await api.get('/dev/chronos-engine/state', {
      params: { escolaId, turnoId },
    });
    return data;
  },
};

